
import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRefs,
  watch,
  nextTick,
  onMounted,
} from "vue";
import { TranslationService } from "@/core/services/TranslationService";
import { hideModal } from "@/core/helpers/dom";
import { useToast } from "vue-toastification";
import { Form, Field, ErrorMessage, FieldArray } from "vee-validate";
import * as Yup from "yup";
import { UserAPI } from "@/services/api/User.service";
import { ICreateUserModel, IUser } from "@/interfaces/iUser";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
// import VZMultiSelect from "@/components/modals/forms/VZMultiSelect.vue";
import { IUserPhoneModel } from "@/interfaces/iCurrentUser";
import { hasPermission } from "@/core/services/permission.service";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment-timezone";
import VZPhone from "@/components/VZPhone.vue";
import AddSiteModal from "../modal/AddSiteModal.vue";
import { getLookups } from "@/services/api/Lookup.service";

export default defineComponent({
  name: "clone-user",
  emits: ["close", "update:modelValue"],
  components: {
    Form,
    Field,
    ErrorMessage,
    // VZMultiSelect,
    FieldArray,
    Datepicker,
    VZPhone,
    AddSiteModal,
  },
  props: {
    modelValue: { type: Boolean, default: false },
    selectedForAction: {
      type: Object as PropType<IUser>,
      required: true,
    },
    languageOption: {
      type: Object as PropType<{ label: any; value: string }>,
      // required: true,
    },
    siteOption: {
      type: Array as () => Array<{ label: any; value: string }>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const message = t("pages.user.userUpdatedMessage");
    const submitButton = ref<HTMLButtonElement | null>(null);
    const { selectedForAction, modelValue } = toRefs(props);
    const userSiteModels = ref<string[]>([]);
    const phoneModel = ref<IUserPhoneModel[]>();
    const formRef = ref<null | HTMLFormElement>(null);
    const defaultPhone = ref<number>();
    const loading = ref(false);
    const password = ref("");
    const password_confirmation = ref("");
    const showSiteDialog = ref(false);
    const roleOptions = ref([]) as any;
    const siteTableData = ref([]) as any;
    const siteOptions = ref([]) as any;
    const showErrorSite = ref(false);
    let permissionModel = ref<any>({
      create: "VYZOR:USERS:USERS:CREATE",
      delete: "VYZOR:USERS:USERS:DELETE",
      update: "VYZOR:USERS:USERS:UPDATE",
    });
    const cloneUserModel = ref<Partial<ICreateUserModel>>({
      firstName: "",
      lastName: "",
      status: "Active",
      email: "",
      phoneNumber: "",
      language: "",
      profilePicture: "",
      birthday: "",
      userPhoneModels: [],
      // useSiteModels: [],
    });
    onMounted(() => {
      getLookups(`security/filter/ROLES`)
        .then((res) => {
          res.map((data) =>
            roleOptions.value.push({
              label: data.text,
              value: data.value,
            })
          );
        })
        .catch((err) => {
          console.error(err);
        });
    });
    const siteIdsOptions = computed(() => {
      return (
        props.siteOption &&
        props.siteOption.map((item) => {
          return {
            label: item.label,
            value: item.value,
          };
        })
      );
    });
    const addDataToSiteTable = (newVal) => {
      siteTableData.value = [];
      newVal.userSites.forEach((site) => {
        const matchedRole = newVal.userRoles.find(
          (role) => role.user_role_id == site.user_role_id
        );
        if (matchedRole) {
          siteTableData.value.push({
            siteName: site.site_name,
            roleName: matchedRole.role_name,
            status: "Active",
            id: site.site_id,
            roleId: matchedRole.role_id,
          });
        }
      });

      const siteIdsInTable = siteTableData.value.map((item) =>
        item.id.toString()
      );
      const filteredSiteIdsOptions = siteIdsOptions.value.filter(
        (item) => !siteIdsInTable.includes(item.value)
      );
      siteOptions.value = filteredSiteIdsOptions;
    };
    const removeSite = (item, index) => {
      siteTableData.value.splice(index, 1);
      const siteIdsInTable = siteTableData.value.map((item) =>
        item.id.toString()
      );
      const filteredSiteIdsOptions = siteIdsOptions.value.filter(
        (item) => !siteIdsInTable.includes(item.value)
      );
      siteOptions.value = filteredSiteIdsOptions;
    };

    const submitSiteData = (data) => {
      const roleName = roleOptions.value.find(
        (option) => option.value == data[0].roleId
      )?.label;
      const roleId = roleOptions.value.find(
        (option) => option.value == data[0].roleId
      )?.value;
      data.forEach((item) => {
        const matchedItem = siteIdsOptions.value.find(
          (elem) => elem.value === item.siteId
        );
        if (matchedItem) {
          siteTableData.value.push({
            siteName: matchedItem.label,
            roleName: roleName,
            status: "Active",
            id: matchedItem.value,
            roleId: roleId,
          });
        }
      });
      const siteIdsInTable = siteTableData.value.map((item) => Number(item.id));
      const filteredSiteIdsOptions = siteIdsOptions.value.filter(
        (item) => !siteIdsInTable.includes(Number(item.value))
      );
      siteOptions.value = filteredSiteIdsOptions;
      showSiteDialog.value = false;
    };
    const statusOptions = [
      {
        label: t("common.active"),
        value: "Acitve",
      },
      {
        label: t("common.inactive"),
        value: "Inactive",
      },
    ];
    const format = computed(() => {
      return FormatMethods;
    });
    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(cloneUserModel.value.startDate).format(
        "YYYY-MM-DD"
      );
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });
    const initialValues = computed(() => {
      if (!phoneModel?.value?.length) {
        return {};
      }
      return {
        phoneModel: phoneModel?.value ? [...phoneModel?.value] : [],
      };
    });
    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "yyyy-MM-dd";
    });
    const discard = () => {
      var appElement = document.getElementById("app");

      if (appElement) {
        appElement.click();
      }
      addDataToSiteTable(selectedForAction.value);
      emit("update:modelValue", false);
    };
    const cloneUser = async (values) => {
      try {
        if (siteTableData.value.length < 1) {
          showErrorSite.value = true;
          return;
        }
        if (validationErrors.value.includes(true)) {
          return;
        }
        // eslint-disable-next-line
        if (validationErrors.value.includes(true)) {
          return;
        }
        if (
          cloneUserModel.value.endDate &&
          cloneUserModel.value.startDate &&
          cloneUserModel.value.endDate < cloneUserModel.value.startDate
        ) {
          return;
        }
        submitButton.value!.disabled = true;

        const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
        if (
          cloneUserModel.value &&
          cloneUserModel.value.startDate &&
          cloneUserModel.value.birthday
        ) {
          const startMoment = moment(cloneUserModel?.value?.startDate).startOf(
            "day"
          );
          const formattedStartDate = startMoment.format(dateFormat);
          cloneUserModel.value.startDate = formattedStartDate;
          const formattedBirthday = moment(
            cloneUserModel?.value?.birthday
          ).format(dateFormat);
          cloneUserModel.value.birthday = formattedBirthday;
        } else {
          return;
        }
        if (cloneUserModel.value.endDate) {
          const endMoment = moment(cloneUserModel.value.endDate).endOf("day");
          const formattedEndDate = endMoment.format(dateFormat);
          cloneUserModel.value.endDate = formattedEndDate;
        }

        submitButton.value?.setAttribute("data-kt-indicator", "on");
        const { webId, birthday, startDate, endDate } =
          cloneUserModel.value as any;
        const userSiteModels =
          siteTableData.value?.map((item) => {
            return {
              site: item.id,
              roleId: item.roleId,
            };
          }) ?? [];
        const userPhoneModels = values?.phoneModel?.map((phone, index) => {
          return {
            phoneModel: {
              phoneNumber: phone.phoneNumber?.replace(/[^\d+]/g, ""),
              type: phone.type,
              // webId: phone.webId,
              defaultPhone: index == values.defaultPhone,
            },
          };
        });
        await UserAPI.getInstance().updateUser({
          webId,
          firstName: values.firstName,
          lastName: values.lastName,
          email: cloneUserModel.value.email ?? "",
          birthday,
          startDate,
          endDate,
          phoneNumber: "",
          profilePicture: cloneUserModel.value.profilePicture ?? "",
          userSiteModels,
          userPhoneModels,
          password: password.value,
        } as any);
        const cloneModal = document.querySelector(
          `#user__UpdateUserModal`
        ) as HTMLElement;

        emit("close");
        toast.success(message);
        discard();
        hideModal(cloneModal);
      } finally {
        submitButton.value?.removeAttribute("data-kt-indicator");
        // eslint-disable-next-line
        submitButton.value!.disabled = false;
      }
    };
    const login = Yup.object().shape({
      firstName: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.firstName"), 30))
        .required(t("common.firstName") + " " + t("common.isRequired"))
        .matches(/^[A-Za-z\s]+$/, t("validation.notStartWithNum"))
        .nullable(),
      lastName: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.lastName"), 30))
        .required(t("common.lastName") + " " + t("common.isRequired"))
        .matches(/^[A-Za-z\s]+$/, t("validation.notStartWithNum"))
        .nullable(),
      // startDate: Yup.string().nullable().label(t("common.startDate")),
      password: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("common.password"), 30))
        .label(t("common.password"))
        .matches(
          /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&*()]).{8,}/,
          t("validation.passwordValidation")
        ),
      password_confirmation: Yup.string()
        .max(30, getMaxLengthErrorMessage(t("pages.user.confirmPassword"), 30))
        .oneOf([Yup.ref("password"), null], "Password does not match")
        .label(t("pages.user.confirmPassword")),
      defaultPhone: Yup.string().label(t("common.defaultPhone")),
      birthday: Yup.string()
        .nullable()
        .test("valid-birthday", t("validation.birthday"), function (value) {
          if (
            cloneUserModel.value &&
            cloneUserModel.value.birthday &&
            cloneUserModel.value.birthday.length < 1
          ) {
            return this.createError({
              path: this.path,
              message: "Birthday is required",
            });
          }
          const selectedDate = moment(cloneUserModel.value.birthday);
          const minBirthdate = moment().subtract(16, "years");

          if (selectedDate.isValid() && selectedDate.isAfter(minBirthdate)) {
            return false; // Fail the validation
          }
          return true; // Pass the validation
        }),
      phoneModel: Yup.array().of(
        Yup.object().shape({
          phoneNumber: Yup.string().label(t("pages.user.phoneNumber")),
        })
      ),
    });
    const setFormValues = () => {
      validationErrors.value = [];
      loading.value = true;
      const { ...rest } = selectedForAction.value;
      nextTick(async () => {
        formRef?.value?.resetForm();
        phoneModel.value = [];
        userSiteModels.value = [];
        cloneUserModel.value = rest;
        if (rest.startDate) {
          cloneUserModel.value.startDate = format.value.convertUTCToLocalDate(
            rest.startDate,
            "YYYY-MM-DD"
          );
        } else {
          cloneUserModel.value.startDate = "";
        }
        if (rest.endDate) {
          cloneUserModel.value.endDate = format.value.convertUTCToLocalDate(
            rest.endDate,
            "YYYY-MM-DD"
          );
        }
        cloneUserModel.value.status =
          cloneUserModel.value &&
          cloneUserModel.value.status &&
          cloneUserModel.value.status.charAt(0).toUpperCase() +
            cloneUserModel.value.status.slice(1).toLowerCase();
        defaultPhone.value =
          selectedForAction.value?.userPhone?.findIndex((phone, index) => {
            return phone?.default_phone;
          }) ?? 0;

        phoneModel.value = selectedForAction.value?.userPhone?.map((phone) => {
          return {
            phoneNumber: phone?.phone_number ?? "",
            type: "CALL",
            defaultPhone: phone?.default_phone ?? false,
          };
        });
        loading.value = false;
        await nextTick();
        userSiteModels.value = [
          ...(rest.userSites?.map((item) => `${item.site_id}`) ?? []),
        ];
        // 419552
      });
    };
    watch(selectedForAction, (newVal) => {
      if (selectedForAction.value) {
        addDataToSiteTable(newVal);
        setFormValues();
      } else {
        phoneModel.value = [];
        userSiteModels.value = [];
      }
    });

    watch(
      siteIdsOptions,
      (newVal, oldVal) => {
        siteOptions.value = newVal;
      },
      { deep: true }
    );

    const validationErrors = ref([]) as any;
    const handleValidation = (value, index) => {
      validationErrors.value[index] = value;
    };

    return {
      t,
      cloneUser,
      login,
      submitButton,
      cloneUserModel,
      statusOptions,
      userSiteModels,
      format,
      phoneModel,
      initialValues,
      loading,
      displayDate,
      formRef,
      defaultPhone,
      minDate,
      minEndDate,
      password_confirmation,
      password,
      permissionModel,
      hasPermission,
      defaultPhoneIndex: 0,
      discard,
      validationErrors,
      handleValidation,
      showSiteDialog,
      roleOptions,
      submitSiteData,
      siteTableData,
      removeSite,
      siteOptions,
      siteIdsOptions,
      addDataToSiteTable,
      showErrorSite,
    };
  },
});
